<div class="container-fluid py-4 create-employee-page" style="max-width: 1200px">
  <!-- Hero Banner -->
  <div class="booking-hero card border-0 mb-4">
    <div class="booking-hero__content">
      <div>
        <div class="booking-hero__eyebrow">Personnel Directory</div>
        <h2 class="booking-hero__title mb-1">Create Practice Employee</h2>
        <div class="booking-hero__meta">
          <span
            ><i class="bi bi-person-plus-fill me-1 text-primary"></i>Register a new staff member and
            assign role permissions</span
          >
        </div>
      </div>
      <div class="booking-hero__status">
        <button class="btn btn-outline-secondary" (click)="cancel()">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>
  </div>

  <!-- Receptionist Limit Warning -->
  @if (receptionistCapReached) {
    <div
      class="alert alert-danger d-flex align-items-start mb-4 p-3 rounded-3 shadow-sm border-danger border-start border-4"
      role="alert"
    >
      <i class="bi bi-exclamation-octagon-fill fs-4 me-3 text-danger flex-shrink-0 mt-1"></i>
      <div class="flex-grow-1">
        <div class="fw-bold text-danger">Receptionist Limit Reached</div>
        <div class="small text-dark mt-1">
          This practice has reached its maximum capacity of <strong>5 active receptionists</strong>.
          You must deactivate an existing receptionist before creating a new one.
        </div>
      </div>
    </div>
  }

  <!-- Prominent Error Alert Banner -->
  @if (errorMessage) {
    <div
      class="alert alert-danger d-flex align-items-start mb-4 p-3 rounded-3 shadow-sm border-danger border-start border-4"
      role="alert"
    >
      <i class="bi bi-exclamation-octagon-fill fs-4 me-3 text-danger flex-shrink-0 mt-1"></i>
      <div class="flex-grow-1">
        <div class="fw-bold text-danger">Employee Registration Failed</div>
        <div class="small text-dark mt-1">{{ errorMessage }}</div>
      </div>
      <button
        type="button"
        class="btn-close ms-2"
        (click)="errorMessage = null"
        aria-label="Close error message"
      ></button>
    </div>
  }

  <form #employeeForm="ngForm" (ngSubmit)="employeeForm.valid && submit()">
    <div class="alert alert-info d-flex align-items-start employee-note mb-4">
      <i class="bi bi-info-circle-fill me-2 mt-1 fs-5"></i>
      <div>
        Staff receive system access permissions based on their assigned role. Provide contact
        information needed to support practice operations.
      </div>
    </div>

    <div class="row g-4">
      <!-- Personal Details -->
      <div class="col-12 col-lg-6">
        <div class="card booking-card h-100">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Identity & Role</div>
              <h4 class="sub-title mb-0">Personal Details</h4>
            </div>
          </div>

          <div class="card-body p-4">
            <div class="row g-3">
              <div class="col-12 col-sm-4">
                <label for="employeeTitle" class="form-label"
                  >Title <span class="text-danger">*</span></label
                >
                <select
                  id="employeeTitle"
                  class="form-select"
                  name="title"
                  [(ngModel)]="form.title"
                  required
                >
                  @for (t of titles; track t) {
                    <option [value]="t">{{ t }}</option>
                  }
                </select>
              </div>

              <div class="col-12 col-sm-8">
                <label for="employeeFirstName" class="form-label"
                  >First Name <span class="text-danger">*</span></label
                >
                <input
                  id="employeeFirstName"
                  type="text"
                  class="form-control"
                  name="firstName"
                  [(ngModel)]="form.firstName"
                  required
                  placeholder="e.g. Jane"
                  #firstName="ngModel"
                />
                @if (firstName.invalid && firstName.touched) {
                  <div class="text-danger small mt-1">First name is required.</div>
                }
              </div>

              <div class="col-12">
                <label for="employeeLastName" class="form-label"
                  >Last Name <span class="text-danger">*</span></label
                >
                <input
                  id="employeeLastName"
                  type="text"
                  class="form-control"
                  name="lastName"
                  [(ngModel)]="form.lastName"
                  required
                  placeholder="e.g. Smith"
                  #lastName="ngModel"
                />
                @if (lastName.invalid && lastName.touched) {
                  <div class="text-danger small mt-1">Last name is required.</div>
                }
              </div>

              <div class="col-12">
                <label for="employeeEmail" class="form-label"
                  >Email Address <span class="text-danger">*</span></label
                >
                <input
                  id="employeeEmail"
                  type="email"
                  class="form-control"
                  name="email"
                  [(ngModel)]="form.email"
                  required
                  email
                  placeholder="e.g. jane.smith@practice.com"
                  #email="ngModel"
                />
                @if (email.invalid && email.touched) {
                  <div class="text-danger small mt-1">
                    @if (email.errors?.['required']) {
                      Email is required.
                    } @else {
                      Enter a valid email address.
                    }
                  </div>
                }
              </div>

              <div class="col-12 col-sm-6">
                <label for="employeePhoneNumber" class="form-label"
                  >Phone Number <span class="text-danger">*</span></label
                >
                <input
                  id="employeePhoneNumber"
                  type="tel"
                  class="form-control"
                  name="phoneNumber"
                  [(ngModel)]="form.phoneNumber"
                  required
                  pattern="^0[0-9]{9}$"
                  maxlength="10"
                  inputmode="numeric"
                  placeholder="e.g. 0821234567"
                  #phoneNumber="ngModel"
                />
                @if (phoneNumber.invalid && phoneNumber.touched) {
                  <div class="text-danger small mt-1">
                    @if (phoneNumber.errors?.['required']) {
                      Phone number is required.
                    } @else {
                      Enter a 10-digit phone number starting with 0.
                    }
                  </div>
                }
              </div>

              <div class="col-12 col-sm-6">
                <label for="employeeGender" class="form-label"
                  >Gender <span class="text-danger">*</span></label
                >
                <select
                  id="employeeGender"
                  class="form-select"
                  name="gender"
                  [(ngModel)]="form.gender"
                  required
                >
                  @for (g of genders; track g) {
                    <option [value]="g">{{ g }}</option>
                  }
                </select>
              </div>

              <div class="col-12">
                <label for="employeeRole" class="form-label"
                  >Role Permission <span class="text-danger">*</span></label
                >
                <select
                  id="employeeRole"
                  class="form-select"
                  name="role"
                  [(ngModel)]="form.role"
                  required
                >
                  @for (r of roles; track r) {
                    <option [value]="r">{{ formatRole(r) }}</option>
                  }
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Address Details -->
      <div class="col-12 col-lg-6">
        <div class="card booking-card h-100">
          <div class="card-header booking-card__header">
            <div>
              <div class="booking-card__eyebrow">Residential Location</div>
              <h4 class="sub-title mb-0">Address Details</h4>
            </div>
          </div>

          <div class="card-body p-4">
            <div class="row g-3">
              <div class="col-12">
                <label for="employeeAddressLine1" class="form-label">Address Line 1</label>
                <input
                  id="employeeAddressLine1"
                  type="text"
                  class="form-control"
                  name="addressLine1"
                  [(ngModel)]="form.addressLine1"
                  placeholder="Building and street address"
                />
              </div>

              <div class="col-12">
                <label for="employeeAddressLine2" class="form-label">Address Line 2</label>
                <input
                  id="employeeAddressLine2"
                  type="text"
                  class="form-control"
                  name="addressLine2"
                  [(ngModel)]="form.addressLine2"
                  placeholder="Apartment, suite, unit (optional)"
                />
              </div>

              <div class="col-12">
                <label for="employeeAddressLine3" class="form-label">Address Line 3</label>
                <input
                  id="employeeAddressLine3"
                  type="text"
                  class="form-control"
                  name="addressLine3"
                  [(ngModel)]="form.addressLine3"
                  placeholder="Suburb / area (optional)"
                />
              </div>

              <div class="col-12 col-sm-8">
                <label for="employeeCity" class="form-label">City</label>
                <input
                  id="employeeCity"
                  type="text"
                  class="form-control"
                  name="city"
                  [(ngModel)]="form.city"
                  placeholder="e.g. Cape Town"
                />
              </div>

              <div class="col-12 col-sm-4">
                <label for="employeePostalCode" class="form-label">Postal Code</label>
                <input
                  id="employeePostalCode"
                  type="text"
                  class="form-control"
                  name="postalCode"
                  [(ngModel)]="form.postalCode"
                  placeholder="e.g. 8001"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions Sticky Bar -->
    <div class="actions actions--sticky d-flex justify-content-between align-items-center mt-4">
      <button type="button" class="btn btn-outline-secondary px-4" (click)="cancel()">
        Cancel
      </button>

      <button
        type="submit"
        class="btn btn-primary px-4"
        [disabled]="
          isSubmitting ||
          employeeForm.invalid ||
          (form.role === 'reception' && receptionistCapReached)
        "
      >
        @if (isSubmitting) {
          <span class="spinner-border spinner-border-sm me-2" role="status"></span>Creating...
        } @else if (form.role === 'reception' && receptionistCapReached) {
          <i class="bi bi-lock-fill me-1"></i> Receptionist Limit Reached
        } @else {
          <i class="bi bi-person-plus me-1"></i> Create Employee
        }
      </button>
    </div>
  </form>
</div>
